import { useEffect } from "react";

import {
  selectConfigItem,
  selectLanguage,
  setMapFont,
} from "@/features/config/configSlice";
import { selectFontSource, setFontSource } from "@/features/map/mapSlice";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import type { FontMap } from "@/features/config/types";
import { DEFAULT_GLYPH_SOURCE } from "@/features/map/constants";
import {
  FONT_BY_LANGUAGE,
  getFontNameFromURL,
  getFontSourceFromURL,
  loadFont,
} from "@/libs/fonts";

const useFontStyle = () => {
  const dispatch = useAppDispatch();
  const configuredFonts = useAppSelector(
    selectConfigItem("styles.theme.fonts"),
  );
  const fontSize = useAppSelector(selectConfigItem("ui.fontSize"));
  const lang = useAppSelector(selectLanguage);
  const fontSource = useAppSelector(selectFontSource);
  const fontFamily = useAppSelector(selectConfigItem("styles.map_font"));

  const setDefaultFonts = () => {
    const fontsToLoad = [
      lang ? FONT_BY_LANGUAGE[lang] : null,
      FONT_BY_LANGUAGE.default,
    ].filter(Boolean);
    const fontNames = fontsToLoad.map((font) => font.fontName).join(",");
    document.documentElement.style.setProperty(
      "--ui-font-family",
      `"${fontNames}"`,
    );
    // Define map font
    dispatch(setMapFont(fontNames));

    // Set the glyph source for the map
    dispatch(setFontSource(DEFAULT_GLYPH_SOURCE));
  };

  // Load custom fonts into html
  useEffect(() => {
    configuredFonts?.forEach((font: FontMap) => {
      font.lang && font.url && loadFont(getFontNameFromURL(font.url), font.url);
    });
  }, [configuredFonts]);

  // Set which fonts are going to be used
  useEffect(() => {
    const locale = lang ? new Intl.Locale(lang) : null;
    const currentFont = configuredFonts.find(
      (f) => f.lang === locale?.language,
    );

    // If we have custom font
    if (currentFont?.url) {
      const fontName = getFontNameFromURL(currentFont.url);
      //set font
      document.documentElement.style.setProperty(
        "--ui-font-family",
        `"${fontName}"`,
      );
      dispatch(setFontSource(getFontSourceFromURL(currentFont.url)));
      dispatch(setMapFont(fontName));
    } else {
      // Else load default fonts
      setDefaultFonts();
    }
  }, [lang, configuredFonts]);

  // Check if font exsits, else fallback to default fonts
  useEffect(() => {
    if (!fontFamily || !fontSource) return;
    const fontTestURL = fontSource
      .replace("{fontstack}", fontFamily)
      .replace("{range}", "0-255");

    fetch(fontTestURL)
      .then((r) => {
        if (!r.ok || r.status < 200 || r.status >= 300) {
          console.warn(`Could not load font ${fontTestURL}`);
          dispatch(setFontSource(DEFAULT_GLYPH_SOURCE));
          setDefaultFonts();
        }
      })
      .catch((e) => {
        console.warn(`Could not load font ${fontTestURL}: ${e}`);
        dispatch(setFontSource(DEFAULT_GLYPH_SOURCE));
        setDefaultFonts();
      });
  }, [fontSource, fontFamily]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--font-size-base",
      `var(--font-size-base-${fontSize})`,
    );
  }, [fontSize]);
};

export default useFontStyle;
